import appSettingsHistoryIdsMutations from '@/components/settings-menu/historyIds.mutations.js';
import appSettingsMatchingBrandsMutations from '@/components/settings-menu/matchingBrands.mutations.js';
import appSettingsMinLastPriceUpdateMutations from '@/components/settings-menu/minLastPriceUpdate.mutations.js';
import appSettingsSellersIdsMutations from '@/components/settings-menu/sellersIds.mutations.js';
import Vue from 'vue';
import storage from '../local-storage';

/* Load the mutations for the app settings */
const { loadAppSettingsHistoryIds, setAppSettingsHistoryIds } = appSettingsHistoryIdsMutations(storage);
const { loadAppSettingsMatchingBrands, setAppSettingsMatchingBrands } = appSettingsMatchingBrandsMutations(storage);
const { loadAppSettingsMinLastUpdateFromStorage, setAppSettingsMinLastPriceUpdate } = appSettingsMinLastPriceUpdateMutations(storage);
const { loadAppSettingsClientsSellersIdsFromStorage, setAppSettingsClientsSellersIds } = appSettingsSellersIdsMutations(storage, 'clients');
const { loadAppSettingsCompetitorsSellersIdsFromStorage, setAppSettingsCompetitorsSellersIds } = appSettingsSellersIdsMutations(storage, 'competitors');

/**
 * Clear the selected items array and set a new empty array.
 * @param {Object} state
 * @param {Object[]} items 
 */
function clearSelectedItems(state) {
  const items = [...state.selectedItems]
  state.selectedItems = []
  items.forEach(item => setSimilarItemSelected(state, { item, selected: false }))
}


/**
 * Clear all the filters for the similar items.
 * @param {Object} state
 */
function clearSimilarItemsFilters(state) {
  const finalObject = {}
  Object.entries(state.similarItemsFilters).map(([key, value]) => {
    finalObject[key] = Array.isArray(value) ? [] : null;
  })

  state.similarItemsFilters = finalObject;
}


/**
 * Load the app settings and other values for the store from the local storage.
 *
 * @param {Object} context
 */
function loadSettingsFromLocalStorage(state) {
  /* Load the app's settings */
  loadAppSettingsHistoryIds(state)
  loadAppSettingsMatchingBrands(state)
  loadAppSettingsMinLastUpdateFromStorage(state)
  loadAppSettingsClientsSellersIdsFromStorage(state)
  loadAppSettingsCompetitorsSellersIdsFromStorage(state)

  /* The size of the page of the similar items */
  const similarItemsFetch = storage.getItem('similarItemsFetch')
  if (similarItemsFetch) {
    setSimilarItemsFetch(state, similarItemsFetch)
  }

  /* If we want to see only the pending classifications */
  const showOnlyPendingClassifications = storage.getItem('showOnlyPendingClassifications')
  if (showOnlyPendingClassifications) {
    setShowOnlyPendingClassifications(state, showOnlyPendingClassifications === 'true')
  }
}


/**
 * Set the brands of the current selected classification.
 * @param {Object} state
 * @param {Object[]} brands
 */
function setBrands(state, brands) {
  state.brands = brands
}


/**
 * Set the string to search in the brands table.
 *
 * @param {Object} state
 * @param {String} search
 */
function setBrandsSearch(state, search) {
  state.brandsSearch = search
}


/**
 * Set the classifications in the store. Avoid using this mutation directly,
 * please use the `getClassifications` action.
 * @param {Object} state
 * @param {Object[]} classifications
 */
function setClassifications(state, classifications) {
  state.classifications = classifications
  setBrands(state, [])
}


/**
 * Set the search string to search in the classifications table.
 * @param {Object} state
 * @param {String} search
 */
function setClassificationsSearch(state, search) {
  state.classificationsSearch = search
}


/**
 * Set the visibility of the individual qmps card component. Changing this value
 * shows or hides the card.
 * @param {Object} state
 * @param {Boolean} visible
 */
function setIndividualQmpsCardVisible(state, visible) {
  state.individualQmpsCardVisible = visible
  clearSelectedItems(state)
  setSidebarVisible(state, !visible)
  setSimilarItemsHideThoseWithQmp(state, visible)
}


/**
 * Set the flag that indicates that the app is getting the brands. Avoid using this mutation
 * because is used by the `getBrands` action.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingBrands(state, loading) {
  state.loadingBrands = loading
}


/**
 * Set the flag that indicates if the app is getting the classifications to populate
 * the store. Avoid using this mutation, it must be used by the `getClassifications`
 * action.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingClassifications(state, loading) {
  state.loadingClassifications = loading
}


/**
 * Set the flag that indicates that the action `createIndex` is running.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingIndexCreation(state, loading) {
  state.loadingIndexCreation = loading
}


/**
 * Set the flag that indicates that the app is loading the items to use as pivots.
 * Avoid using this mutations, it must be used by the `getPivotItems` action.
 * @param {Object} state
 * @param {Boolean} loading 
 */
function setLoadingPivotItems(state, loading) {
  state.loadingPivotItems = loading
}


/**
 * Set the flag that indicates that the app is loading the QMatchProduct creation.
 * Avoid using this mutation directly, is only used by the action `createProduct`.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingProductCreation(state, loading) {
  state.loadingProductCreation = loading
}


/**
 * Set the flag that indicates that the app is loading the QMPs. Avoid using this mutation
 * because is only used by the `getQmps` action.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingQmps(state, loading) {
  state.loadingQmps = loading
}


/**
 * Set the flag that indicates that the app is removing a QMI from a QMP.
 * Do not use this mutation, is used by the `removeQmiFromQmp` action.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingRemoveQmiFromQmp(state, loading) {
  state.loadingRemoveQmiFromQmp = loading
}


/**
 * Set the flag that indicates that a QMP is being deleted.
 * @param {OBject} state
 * @param {Boolean} loading
 */
function setLoadingRemoveQmp(state, loading) {
  state.loadingRemoveQmp = loading
}


/**
 * Set the flag that indicates that the app is getting the matching items for the pivot one.
 * Avoid using this mutation, it must be used by the `getSimilarItems` action.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingSimilarItems(state, loading) {
  state.loadingSimilarItems = loading
}


/**
 * Set the flag that indicates that the app is loading the QMPs of the similar items.
 * @param {Object} state
 * @param {Boolean} loading
 */
function setLoadingSimilarItemsQmps(state, loading) {
  state.loadingSimilarItemsQmps = loading
}


/**
 * Set the pivot items in the store. Avoid using this mutation, it must be used by
 * the `getPivotItems` action.
 * @param {Object} state
 * @param {Object[]} pivots
 */
function setPivotItems(state, pivots) {
  state.pivotItems = pivots
}


/**
 * Set the number of items that the `getPivotItems` action should get.
 * @param {Object} state
 * @param {Number} fetch
 */
function setPivotItemsFetch(state, fetch) {
  state.pivotItemsFetch = fetch
}


/**
 * Set the number of items that the action `getPivotItems` should get.
 * Avoid using this mutation directly, instead use the `page` argument for the action.
 * @param {Object} state
 * @param {Number} offset
 */
function setPivotItemsOffset(state, offset) {
  state.pivotItemsOffset = offset
}


/**
 * Set the total number of pivot items available. Avoid using this mutation directly
 * because it must be used by the `getPivotItems` action.
 * @param {Object} state
 * @param {Number} total
 */
function setPivotItemsTotal(state, total) {
  state.pivotItemsTotal = total
}

/**
 * Set the qmps in the store. Avoid using this mutation, it's used by the `getQmps` action.
 * @param {Object} state
 * @param {Object[]} qmps
 */
function setQmps(state, qmps) {
  state.qmps = qmps
}


/**
 * Set the number of qmps to get in a single action `getQmps` call.
 * @param {Object} state
 * @param {Number} fetch
 */
function setQmpsFetch(state, fetch) {
  state.qmpsFetch = fetch
}


/**
 * Set the number of QMPs that the action `getQmps` must skip to get the `fetch`
 * number of qmps. It's recommended to use the `page` argument of the `getQmps` action
 * instead.
 * @param {Object} state
 * @param {Number} offset
 */
function setQmpsOffset(state, offset) {
  state.qmpsOffset = offset
}


/**
 * Set the number of qmps that are available to get from the backend. Avoid using this
 * mutation, is used by the `getQmps` action.
 * @param {Object} state
 * @param {Number} total
 */
function setQmpsTotal(state, total) {
  state.qmpsTotal = total
}


/**
 * Show or hide the classifications without pending items.
 * A pending classification is one that has at least a client or competitor
 * QMI without QMP, so it has items that are not matched yet.
 * @param {Object} state
 * @param {Boolean} show
 */
function setShowOnlyPendingClassifications(state, show) {
  state.showOnlyPendingClassifications = show
  storage.setItem('showOnlyPendingClassifications', show)
}


/**
 * Set the visible value of the sidebar that contains the pivot and selected items.
 * @param {Object} state
 * @param {Boolean} visible
 */
function setSidebarVisible(state, visible) {
  state.sidebarVisible = visible
}


/**
 * Set the items that are similar to the pivots. Avoid using this mutation directly, instead
 * please use the `getSimilarItems` action.
 * @param {Object} state
 * @param {Object[]} items
 */
function setSimilarItems(state, items) {
  state.similarItems = items.map(item => {
    let isSelected = state.selectedItems.some(i => i.sellerId === item.sellerId && i.historyId === item.historyId);
    state.selectedItems.forEach(selectedItem => {
      if (!selectedItem.homologedItems || isSelected) return;
      isSelected = selectedItem.homologedItems.some(i => i.sellerId === item.sellerId && i.historyId === item.historyId)
    });
    return {
      ...item,
      selected: isSelected
    }
  })
}


/**
 * Set the `selected` property of the given item.
 * @param {Object} state
 * @param {Object} payload
 * @param {Object} payload.item
 * @param {Boolean} payload.selected
 */
function setSimilarItemSelected(state, { item, selected }) {
  const similarIndex = state.similarItems.findIndex(i => i.sellerId === item.sellerId && i.historyId === item.historyId)
  // we first check in the selectedItems array
  let selectedIndex = state.selectedItems.findIndex(i => i.sellerId === item.sellerId && i.historyId === item.historyId);
  // then whe loop through selectedItems to check if there are items with homologedItems inside
  state.selectedItems.forEach((selectedItem, index) => {
    if (!selectedItem.homologedItems || selectedIndex !== -1) return;
    // if there are homologedItems inside we check if is inside of that array
    const indexHomologedItem = selectedItem.homologedItems.findIndex(i => i.sellerId === item.sellerId && i.historyId === item.historyId);
    // if inside homologed items exists the item then we store the index of the forEach to avoid innecesary loop executions
    if (indexHomologedItem !== -1) {
      selectedIndex = index;
    }
  });
  const newItem = { ...item, selected }

  /* Update the item in the similarItems array */
  if (similarIndex !== -1) {
    Vue.set(state.similarItems[similarIndex], 'selected', selected)
  }

  /* Add it to the selectedItems array */
  const inSelectedArray = selectedIndex !== -1
  if (selected && !inSelectedArray) {
    state.selectedItems.push(newItem)
  }

  /* Remove it from the selectedItems array */
  if (!selected && inSelectedArray) {
    state.selectedItems.splice(selectedIndex, 1);
  }

  /* Update the item in the similarItemsQmps */
  if (item.productId) {
    const qmps = state.similarItemsQmps
    const qmp = qmps[item.productId]
    qmp.items = qmp.items.map(qmpItem => {
      if (qmpItem.sellerId === item.sellerId && qmpItem.historyId === item.historyId) {
        return { ...qmpItem, selected }
      }
      return qmpItem
    })
    qmps[qmp.id] = qmp
    setSimilarItemsQmps(state, qmps)
  }
}

/**
 * Set the amount of similar items to get using the `getSimilarItems` action.
 * @param {Object} state
 * @param {Number} fetch
 */
function setSimilarItemsFetch(state, fetch) {
  state.similarItemsFetch = fetch
  storage.setItem('similarItemsFetch', fetch)
}


/**
 * Set the like pattern filter over the similar items.
 *
 * @param {Object} state
 * @param {String} likePattern
 */
function setSimilarItemsFiltersLikePatternBrand(state, likePattern) {
  state.similarItemsFilters.likePatternBrand = likePattern
}
function setSimilarItemsFiltersLikePatternName(state, likePattern) {
  state.similarItemsFilters.likePatternName = likePattern
}

/**
 * Set the max price filter to apply over the similar items.
 *
 * @param {Object} state
 * @param {Number} maxPrice
 */
function setSimilarItemsFiltersMaxPrice(state, maxPrice) {
  state.similarItemsFilters.maxPrice = maxPrice;
}


/**
 * Set the min price filter to apply over the similar items.
 *
 * @param {Object} state
 * @param {Number} minPrice
 */
function setSimilarItemsFiltersMinPrice(state, minPrice) {
  state.similarItemsFilters.minPrice = minPrice;
}


/**
 * Set the flag to hide the items that already have a QMP (they have the productId property
 * populated) in the `filteredSimilarItems` getter.
 * @param {Object} state
 * @param {Boolean} hide
 */
function setSimilarItemsHideThoseWithQmp(state, hide) {
  state.similarItemsHideThoseWithQmp = hide
}


/**
 * Set the offset to get the similar items.
 * @param {Object} state
 * @param {Number} offset
 */
function setSimilarItemsOffset(state, offset) {
  state.similarItemsOffset = offset
}


/**
 * Set the value for the order by of the similar items. Could be by "visual" similarity
 * that compares the images or by "text" similarity that compares using the brand and name
 * of the items.
 * @param {Object} state
 * @param {String} orderBy
 */
function setSimilarItemsOrderBy(state, orderBy) {
  state.similarItemsOrderBy = orderBy
}


/**
 * Set the QMPs of the similar items in an special array to know how many items does it have
 * any of them and similar questions. This array only contains the QMPs of the similar QMIs,
 * that is different from the array of Qmps that are used when loading the created qmps.
 * @param {Object} state
 * @param {Object[]} qmps
 */
function setSimilarItemsQmps(state, qmps) {
  Object.values(qmps).forEach(qmp => {
    qmp.items = qmp.items.map(item => {
      if (state.selectedItems.some(i => i.sellerId === item.sellerId && i.historyId === item.historyId)) {
        return { ...item, selected: true }
      }
      return { ...item, selected: false }
    })
  })
  state.similarItemsQmps = qmps
}


/**
 * Set the total number of items to load as similar.
 * @param {Object} state
 * @param {Number} total
 */
function setSimilarItemsTotal(state, total) {
  state.similarItemsTotal = total
}

function loadingMatchRanking(state, loading) {
  state.loadingMatchRanking = loading;
}
function matchRanking(state, ranking) {
  state.matchRanking = ranking;
}
function setShowMatchRankingSidebar(state, show) {
  state.showMatchRankingSidebar = show;
}

function setSellerHomologations(state, sellerHomologations) {
  state.sellerHomologations = sellerHomologations;
}

export default {
  matchRanking,
  loadingMatchRanking,
  setShowMatchRankingSidebar,
  clearSelectedItems,
  clearSimilarItemsFilters,
  loadSettingsFromLocalStorage,
  setAppSettingsClientsSellersIds,
  setAppSettingsCompetitorsSellersIds,
  setAppSettingsHistoryIds,
  setAppSettingsMatchingBrands,
  setAppSettingsMinLastPriceUpdate,
  setBrands,
  setBrandsSearch,
  setClassifications,
  setClassificationsSearch,
  setIndividualQmpsCardVisible,
  setLoadingBrands,
  setLoadingClassifications,
  setLoadingIndexCreation,
  setLoadingPivotItems,
  setLoadingProductCreation,
  setLoadingQmps,
  setLoadingRemoveQmiFromQmp,
  setLoadingRemoveQmp,
  setLoadingSimilarItems,
  setLoadingSimilarItemsQmps,
  setPivotItems,
  setPivotItemsFetch,
  setPivotItemsOffset,
  setPivotItemsTotal,
  setQmps,
  setQmpsFetch,
  setQmpsOffset,
  setQmpsTotal,
  setShowOnlyPendingClassifications,
  setSidebarVisible,
  setSimilarItems,
  setSimilarItemSelected,
  setSimilarItemsFetch,
  setSimilarItemsFiltersLikePatternBrand,
  setSimilarItemsFiltersLikePatternName,
  setSimilarItemsFiltersMaxPrice,
  setSimilarItemsFiltersMinPrice,
  setSimilarItemsHideThoseWithQmp,
  setSimilarItemsOffset,
  setSimilarItemsOrderBy,
  setSimilarItemsQmps,
  setSimilarItemsTotal,
  setSellerHomologations
}
